import { useCallback, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useBarcodeScanner, type ScannerPhase } from '@/hooks/useBarcodeScanner';
import { parseBowQr } from '@/lib/qrScan';
import { Button } from '@/components/ui/Button';
import { BowArc } from '@/components/ui/BowArc';
import { Eyebrow } from '@/components/ui/Typography';

interface Props {
  onClose: () => void;
  onResolved: (path: string) => void;
}

/**
 * Tela de scan em tela cheia — viewfinder grande, lanterna e "digitar código"
 * (feedback Saulo 2026-07-06, referência Tupi/Spott). Overlay controlado pelo
 * `BottomNav` em vez de rota própria: é uma ferramenta transitória, não um
 * lugar pra onde se navega — fecha sozinha ao resolver ou cancelar.
 */
export function ScanQrOverlay({ onClose, onResolved }: Props) {
  const [toast, setToast] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);

  const handleScanned = useCallback(
    (text: string) => {
      const path = parseBowQr(text);
      if (path) {
        onResolved(path);
      } else {
        setToast('QR não reconhecido — use o QR Bow do carregador ou do aspirador.');
        window.setTimeout(() => setToast(null), 3000);
      }
    },
    [onResolved]
  );

  const {
    phase,
    errorMessage,
    errorDetail,
    torchAvailable,
    torchOn,
    toggleTorch,
    openSettings,
    videoRef
  } = useBarcodeScanner({ onScanned: handleScanned });

  const scanning = phase === 'scanning';
  /** Não há câmera pra mostrar: nada de viewfinder (prometeria uma prévia que
   * não vem) — em vez dele, o motivo e o caminho manual. */
  const cameraDown = phase !== 'starting' && phase !== 'scanning';

  return (
    <div
      className={`barcode-scanner-modal fixed inset-0 z-50 flex flex-col text-white ${
        scanning ? '' : 'bg-black'
      }`}
    >
      {/* Só no web/PWA: prévia de verdade da câmera — ver useBarcodeScanner.
          No app nativo NÃO pode montar: o WebView Android pinta o placeholder
          padrão de mídia num <video> vazio (play gigante borrado) por cima da
          WebView transparente, tampando a câmera nativa que roda atrás
          (bug 2026-07-29, docs/bugs). */}
      {!Capacitor.isNativePlatform() && (
        <video
          ref={videoRef}
          className="absolute inset-0 -z-10 h-full w-full object-cover"
          playsInline
          muted
          aria-hidden
        />
      )}

      <div
        className="flex flex-shrink-0 items-center justify-between px-4 pb-2"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15"
        >
          <CloseIcon />
        </button>
        <span className="text-sm font-semibold">Escanear QR</span>
        <div className="h-10 w-10" aria-hidden />
      </div>

      {!cameraDown && (
        <p className="flex-shrink-0 px-8 text-center text-sm text-white/75">
          Aponte a câmera pro QR do carregador ou do aspirador
        </p>
      )}

      {/* Viewfinder — bem maior que o padrão nativo antigo, área generosa
          pra facilitar o cliente em pé na frente da estação. */}
      <div className="relative flex flex-1 items-center justify-center px-6">
        {cameraDown ? (
          <FallbackPanel
            phase={phase}
            errorMessage={errorMessage}
            errorDetail={errorDetail}
            onOpenSettings={openSettings}
            onManual={() => setManualOpen(true)}
          />
        ) : (
          <>
            <div
              className="aspect-square w-full max-w-[340px] rounded-[32px] border-2 border-white/90"
              style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)' }}
              aria-hidden
            />
            {/* Sem isso, `starting` era uma tela preta sem nada — o cliente não
                sabia se o app estava carregando ou travado. */}
            {phase === 'starting' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <BowArc size={44} spin />
                <span className="text-sm text-white/75">Abrindo a câmera…</span>
              </div>
            )}
          </>
        )}
      </div>

      {toast && (
        <div className="mx-6 mb-4 flex-shrink-0 rounded-2xl bg-white px-4 py-3 text-center text-sm font-semibold text-bow-royal">
          {toast}
        </div>
      )}

      {/* Controles — lanterna + digitar código (pedido explícito, ref. Spott).
          Some quando não há câmera: o painel de fallback já leva pro código. */}
      {!cameraDown && (
        <div className="flex flex-shrink-0 items-center justify-center gap-10 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          {torchAvailable && (
            <ControlButton label="Ativar lanterna" active={torchOn} onClick={toggleTorch}>
              <TorchIcon />
            </ControlButton>
          )}
          <ControlButton label="Digitar código" onClick={() => setManualOpen(true)}>
            <KeyboardIcon />
          </ControlButton>
        </div>
      )}

      {manualOpen && (
        <ManualCodeSheet
          onSubmit={(code) => onResolved(`/scan/${encodeURIComponent(code)}`)}
          onClose={() => setManualOpen(false)}
        />
      )}
    </div>
  );
}

function ControlButton({
  label,
  active = false,
  onClick,
  children
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} className="flex flex-col items-center gap-2">
      <span
        className={`flex h-14 w-14 items-center justify-center rounded-full ${
          active ? 'bg-white text-bow-royal' : 'bg-white/15 text-white'
        }`}
      >
        {children}
      </span>
      <span className="text-xs font-medium text-white/85">{label}</span>
    </button>
  );
}

/**
 * Toda falha da câmera termina aqui: o motivo em linguagem de cliente e uma
 * saída de verdade (o código impresso no carregador). Antes, `unsupported` e
 * `error` eram uma linha de texto sob um viewfinder vazio, e `unavailable` nem
 * existia — o cliente encarava tela preta sem nada pra fazer.
 */
export function FallbackPanel({
  phase,
  errorMessage,
  errorDetail,
  onOpenSettings,
  onManual
}: {
  phase: ScannerPhase;
  errorMessage: string | null;
  errorDetail: string | null;
  onOpenSettings: () => void;
  onManual: () => void;
}) {
  const copy: Record<string, { title: string; body: string }> = {
    denied: {
      title: 'Câmera bloqueada',
      body: 'Permita o acesso à câmera pra escanear o QR — ou use o código impresso no carregador.'
    },
    unavailable: {
      title: 'Câmera indisponível nesta versão',
      body: 'Esta versão do app não consegue abrir a câmera. Atualize o app na loja quando a próxima versão sair. Enquanto isso, use o código impresso no carregador.'
    },
    unsupported: {
      title: 'Sem câmera',
      body: 'Este aparelho não tem câmera disponível. Use o código impresso no carregador.'
    },
    error: {
      title: 'Não deu pra abrir a câmera',
      body: `${errorMessage ?? 'Tente de novo.'} Você pode usar o código impresso no carregador.`
    }
  };
  const { title, body } = copy[phase] ?? copy.error;

  return (
    <div className="flex w-full max-w-[340px] flex-col items-center gap-4 rounded-3xl bg-white/10 p-6 text-center">
      <BowArc size={40} variant="white" />
      <div className="flex flex-col gap-2">
        <p className="text-base font-semibold">{title}</p>
        <p className="text-sm text-white/75">{body}</p>
      </div>
      {phase === 'denied' ? (
        <>
          <Button variant="cream" size="md" onClick={onOpenSettings} fullWidth>
            Abrir configurações
          </Button>
          {/* `ghost` do Button é navy — sumiria neste fundo escuro. */}
          <button
            type="button"
            onClick={onManual}
            className="text-sm font-semibold text-white/85 underline underline-offset-4"
          >
            Digitar código
          </button>
        </>
      ) : (
        <Button variant="cream" size="md" onClick={onManual} fullWidth>
          Digitar código
        </Button>
      )}
      {/* Motivo técnico: sem Mac no fluxo de build não há Safari Web Inspector
          no iPhone, então esta linha é o canal de diagnóstico — o operador
          fotografa a tela e a causa vem junto. */}
      {errorDetail && <p className="text-[11px] leading-tight text-white/40">{errorDetail}</p>}
    </div>
  );
}

function ManualCodeSheet({
  onSubmit,
  onClose
}: {
  onSubmit: (code: string) => void;
  onClose: () => void;
}) {
  const [code, setCode] = useState('');

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex w-full max-w-md flex-col gap-4 rounded-t-3xl bg-white p-6 text-bow-royal"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      >
        <Eyebrow>Código da estação</Eyebrow>
        <p className="text-sm text-bow-royal/65">
          Digite o código impresso no adesivo do carregador.
        </p>
        <input
          type="text"
          inputMode="text"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Ex: 124080002062"
          className="h-12 rounded-2xl border border-bow-royal/15 px-4 text-base focus:border-bow-royal focus:outline-none"
        />
        <Button
          variant="primary"
          size="md"
          onClick={() => onSubmit(code.trim())}
          disabled={!code.trim()}
          fullWidth
        >
          Continuar
        </Button>
        <Button variant="ghost" size="md" onClick={onClose} fullWidth>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function TorchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 2h6l1 4-2 2v10a2 2 0 0 1-2 2 2 2 0 0 1-2-2V8L8 6z" />
      <path d="M9 10h6" />
    </svg>
  );
}

function KeyboardIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
      <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M6 14h12" />
    </svg>
  );
}
